import { useEffect, useRef, useState } from "react";
import { useChatStore } from "../store/useChatStore";
import { useAuthStore } from "../store/useAuthStore";
import { Send, X, FileText, Paperclip, Upload, Smile } from "lucide-react";
import toast from "react-hot-toast";

/* --- Emoji mini set --- */
const EMOJI_BANK = [
  "😀",
  "😁",
  "😂",
  "🤣",
  "😃",
  "😄",
  "😅",
  "😆",
  "😉",
  "😊",
  "🙂",
  "🙃",
  "😋",
  "😎",
  "😍",
  "🥰",
  "😘",
  "😭",
  "😤",
  "😡",
  "🤯",
  "🥳",
  "👍",
  "👎",
  "👌",
  "✌️",
  "🤞",
  "🤟",
  "🤘",
  "👋",
  "🙏",
  "💪",
  "🍕",
  "🍔",
  "🍟",
  "🍩",
  "🍪",
  "🍫",
  "🍰",
  "🎂",
  "🍎",
  "🍇",
  "🍓",
  "🌹",
  "🌸",
  "✨",
  "🔥",
  "🎉",
  "🎊",
  "🐶",
  "🐱",
  "🦄",
  "🌈",
  "⚽",
  "🏀",
  "🎮",
  "📱",
  "💻",
  "📎",
  "📷",
  "✈️",
  "🚗",
  "🏠",
  "⏰",
  "📅",
  "📝",
  "📚",
];

const RECENTS_KEY = "msginput_recent_emojis";
const loadRecents = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
  } catch {
    return [];
  }
};
const saveRecents = (arr) => {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(arr.slice(0, 24)));
  } catch {}
};

export default function CategoryMessageInput({
  replyTo = null,
  onCancelReply = () => {},
  pendingDroppedFiles = [],
  onPendingFilesConsumed = () => {},
  onDropFiles = () => {},
}) {
  const { selectedCategory, sendCategoryMessage, sendMessage, categoryUsers } =
    useChatStore();
  const { authUser } = useAuthStore();

  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [imagePreviewURL, setImagePreviewURL] = useState(null);
  const [showBar, setShowBar] = useState(false);
  const [barPos, setBarPos] = useState({ top: 0, left: 0 });

  const [showEmoji, setShowEmoji] = useState(false);
  const [emojiQuery, setEmojiQuery] = useState("");
  const [recentEmojis, setRecentEmojis] = useState(loadRecents());
  const emojiRef = useRef(null);

  const fileRef = useRef();
  const dropRef = useRef(null);
  const textareaRef = useRef(null);
  const barRef = useRef(null);

  const selStartRef = useRef(0);
  const selEndRef = useRef(0);

  const canCompose = !!selectedCategory;
  const [showMention, setShowMention] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionedIds, setMentionedIds] = useState([]);
  const mentionRef = useRef(null);

  const isImage = (f) => !!f && f.type?.startsWith("image/");
  const isAllowedType = (f) =>
    [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "text/plain",
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/gif",
      "",
    ].includes(f.type);
  const withinSize = (f) => f.size <= 200 * 1024 * 1024;

  const genWAName = (mime = "image/png") => {
    const ext = (mime.split("/")[1] || "png").toLowerCase();
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `WhatsApp Image ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate(),
    )} at ${pad(d.getHours())}.${pad(d.getMinutes())}.${pad(
      d.getSeconds(),
    )}.${ext}`;
  };

  const validateFile = (f) => {
    if (!isAllowedType(f)) {
      toast.error("Unsupported file type");
      return false;
    }
    if (!withinSize(f)) {
      toast.error("File too large (max 200 MB)");
      return false;
    }
    return true;
  };

  const handlePickFile = (f, nameOverride) => {
    if (!canCompose) return;
    if (!f || !validateFile(f)) return;
    setFile(f);
    const clean = (nameOverride ?? f.name ?? "").trim();
    setDisplayName(clean || genWAName(f.type));
  };

  /* Consume pending external drops */
  useEffect(() => {
    if (!pendingDroppedFiles?.length) return;
    const [first, ...rest] = pendingDroppedFiles;
    if (!file && first) {
      handlePickFile(first);
      if (rest.length) {
        toast.success(
          `Attached ${first.name}. ${rest.length} more file(s) ignored (single attach).`,
        );
      }
    }
    onPendingFilesConsumed();
  }, [pendingDroppedFiles]);

  /* Preview */
  useEffect(() => {
    if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
    if (isImage(file)) setImagePreviewURL(URL.createObjectURL(file));
    else setImagePreviewURL(null);
    return () => {
      if (imagePreviewURL) URL.revokeObjectURL(imagePreviewURL);
    };
  }, [file]);

  /* DnD over input bar */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!canCompose) return;
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    onDropFiles(files);
    handlePickFile(files[0]);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    if (canCompose) setIsDragging(true);
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (canCompose) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    if (!dropRef.current) return;
    const r = dropRef.current.getBoundingClientRect();
    const inside =
      e.clientX >= r.left &&
      e.clientX <= r.right &&
      e.clientY >= r.top &&
      e.clientY <= r.bottom;
    if (!inside) setIsDragging(false);
  };

  /* PASTE */
  useEffect(() => {
    const onPaste = (e) => {
      if (!canCompose) return;
      if (!e.clipboardData) return;
      const items = Array.from(e.clipboardData.items || []);
      const imgItem = items.find(
        (it) => it.kind === "file" && it.type.startsWith("image/"),
      );
      if (imgItem) {
        const pasted = imgItem.getAsFile();
        if (!pasted) return;
        handlePickFile(
          pasted,
          (pasted.name || "").trim() || genWAName(pasted.type || "image/png"),
        );
        return;
      }
      const f = e.clipboardData.files?.[0];
      if (f) handlePickFile(f);
    };
    const node = dropRef.current;
    if (!node) return;
    node.addEventListener("paste", onPaste);
    return () => node.removeEventListener("paste", onPaste);
  }, [canCompose]);

  /* Formatting helpers */
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  };
  const captureSelection = () => {
    const el = textareaRef.current;
    if (!el) return;
    selStartRef.current = el.selectionStart ?? 0;
    selEndRef.current = el.selectionEnd ?? 0;
  };
  const restoreSelection = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.selectionStart = selStartRef.current;
    el.selectionEnd = selEndRef.current;
  };
  const insertTextAtCaret = (snippet) => {
    if (!canCompose) return;
    const el = textareaRef.current;
    if (!el) return;
    restoreSelection();
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const next = text.slice(0, start) + snippet + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + snippet.length;
      autoGrow(el);
      maybeShowBar();
    });
  };
  const insertNewlineAtCaret = () => insertTextAtCaret("\n");

  const wrapSelection = (l, r = l) => {
    if (!canCompose) return;
    const el = textareaRef.current;
    if (!el) return;
    restoreSelection();
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (start === end) {
      const next = text.slice(0, start) + l + r + text.slice(end);
      setText(next);
      requestAnimationFrame(() => {
        const pos = start + l.length;
        el.selectionStart = el.selectionEnd = pos;
        autoGrow(el);
        maybeShowBar();
      });
      return;
    }
    const selected = text.slice(start, end);
    const next = text.slice(0, start) + l + selected + r + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el.selectionStart = start + l.length;
      el.selectionEnd = end + l.length;
      autoGrow(el);
      maybeShowBar();
    });
  };

  const applyOnSelectedLines = (mapper) => {
    if (!canCompose) return;
    const el = textareaRef.current;
    if (!el) return;
    restoreSelection();
    let s = el.selectionStart ?? 0;
    let e = el.selectionEnd ?? 0;
    if (s === e) {
      const before = text.lastIndexOf("\n", s - 1) + 1;
      const after = text.indexOf("\n", s);
      s = before < 0 ? 0 : before;
      e = after < 0 ? text.length : after;
    }
    const before = text.slice(0, s);
    const selected = text.slice(s, e);
    const after = text.slice(e);
    const block = selected.split(/\r?\n/).map(mapper).join("\n");
    const next = before + block + after;
    setText(next);
    requestAnimationFrame(() => {
      el.selectionStart = s;
      el.selectionEnd = s + block.length;
      autoGrow(el);
      maybeShowBar();
    });
  };

  const bulletList = () =>
    applyOnSelectedLines((l) =>
      l.trim() ? `- ${l.replace(/^[-*]\s+/, "")}` : l,
    );
  const numberList = () => {
    let i = 1;
    applyOnSelectedLines((l) =>
      l.trim() ? `${i++}. ${l.replace(/^\d+\.\s+/, "")}` : l,
    );
  };

  const monoWrap = () => {
    if (!canCompose) return;
    const el = textareaRef.current;
    if (!el) return;
    restoreSelection();
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const selected = text.slice(start, end);
    if (selected.includes("\n")) wrapSelection("```\n", "\n```");
    else wrapSelection("`", "`");
  };

  const maybeShowBar = () => {
    const el = textareaRef.current;
    if (!el) return;
    captureSelection();
    const hasSel = selStartRef.current !== selEndRef.current;
    setShowBar(canCompose && hasSel);
    const r = el.getBoundingClientRect();
    setBarPos({ top: r.top - 52, left: r.left + 10 });
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const onUp = () => maybeShowBar();
    const onKey = () => setTimeout(maybeShowBar, 0);
    el.addEventListener("mouseup", onUp);
    el.addEventListener("keyup", onKey);
    return () => {
      el.removeEventListener("mouseup", onUp);
      el.removeEventListener("keyup", onKey);
    };
  }, [canCompose]);

  useEffect(() => {
    const onDocDown = (e) => {
      const t = e.target;
      if (textareaRef.current?.contains(t)) return;
      if (barRef.current?.contains(t)) return;
      if (emojiRef.current?.contains(t)) return;
      setShowBar(false);
      setShowEmoji(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  const handleKeyDown = (e) => {
    if (!canCompose) {
      if (e.key === "Enter") e.preventDefault();
      return;
    }
    const meta = e.ctrlKey || e.metaKey;
    if (meta && e.key.toLowerCase() === "b") {
      e.preventDefault();
      wrapSelection("*", "*");
      return;
    }
    if (meta && e.key.toLowerCase() === "i") {
      e.preventDefault();
      wrapSelection("_", "_");
      return;
    }
    if (meta && e.shiftKey && e.key.toLowerCase() === "x") {
      e.preventDefault();
      wrapSelection("~", "~");
      return;
    }
    if (meta && e.key.toLowerCase() === "m") {
      e.preventDefault();
      monoWrap();
      return;
    }
    if (e.key === "Enter") {
      if (e.altKey) {
        e.preventDefault();
        insertNewlineAtCaret();
      } else {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const filteredEmojis = EMOJI_BANK.filter((e) =>
    emojiQuery.trim()
      ? e.toLowerCase().includes(emojiQuery.trim().toLowerCase())
      : true,
  );
  const insertMention = (user) => {
    const el = textareaRef.current;
    if (!el) return;

    const cursor = el.selectionStart;
    const textUntilCursor = text.slice(0, cursor);

    const newText =
      textUntilCursor.replace(/@(\w*)$/, `@${user.name} `) + text.slice(cursor);

    setText(newText);

    setMentionedIds((prev) =>
      prev.includes(user._id) ? prev : [...prev, user._id],
    );

    setShowMention(false);

    setTimeout(() => {
      el.focus();
    }, 0);
  };

  /* SEND */
  const handleSend = async () => {
    if (!canCompose) return;
    if (!text && !file) return;
    if (!selectedCategory) {
      toast.error("No category selected");
      return;
    }

    try {
      const fd = new FormData();
      if (text?.trim()) fd.append("text", text.trim());
      if (file) {
        fd.append("file", file, displayName || file.name || "file");
        if (typeof file.size === "number")
          fd.append("fileSize", String(file.size));
        if (displayName) fd.append("fileName", displayName);
      }
      if (replyTo?._id) {
        fd.append("replyTo", replyTo._id);
        fd.append(
          "replyToSnapshot",
          JSON.stringify({
            text: replyTo.text || "",
            fileName: replyTo.fileName || "",
            senderName: replyTo.senderName || "",
          }),
        );
      }
      if (mentionedIds.length) {
        fd.append("mentions", JSON.stringify(mentionedIds));
      }

      if (typeof sendCategoryMessage === "function") {
        await sendCategoryMessage(fd, selectedCategory);
      } else if (typeof sendMessage === "function") {
        fd.append("receiverId", `category:${selectedCategory}`);
        await sendMessage(fd);
      } else {
        throw new Error("No send method available in store");
      }

      setText("");
      setFile(null);
      setDisplayName("");
      setImagePreviewURL(null);
      setShowBar(false);
      setShowEmoji(false);
      setEmojiQuery("");
      if (fileRef.current) fileRef.current.value = "";
      if (textareaRef.current) textareaRef.current.style.height = "auto";
      onCancelReply?.();
    } catch (err) {
      console.error("Category send failed:", err);
      toast.error(err?.response?.data?.error || "Failed to send message");
    }
    setMentionedIds([]);
  };

  /* MAIN UI */
  return (
    <form
      ref={dropRef}
      onSubmit={(e) => {
        e.preventDefault();
        handleSend();
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      aria-label="Message input area"
      style={{
        position: "sticky",
        bottom: 0,
        left: 0,
        right: 0,
        width: "100%",
        background: "#f5f7fb",
        borderTop: "1px solid #e8ecf2",
        padding: "12px 10px",
        boxSizing: "border-box",
        zIndex: 10,
        bottom: 25,
      }}
    >
      {/* Reply Strip */}
      {replyTo && (
        <div
          style={{
            margin: "0 0 8px",
            padding: "8px 10px",
            borderLeft: "3px solid #8ab4f8",
            background: "rgba(138,180,248,0.12)",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0b3b8c" }}>
              {replyTo.senderName || "Replied message"}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "#1d2127",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={replyTo.text || replyTo.fileName || ""}
            >
              {replyTo.text
                ? replyTo.text
                : replyTo.fileName
                  ? `📄 ${replyTo.fileName}`
                  : "Message"}
            </div>
          </div>

          <button
            type="button"
            onClick={() => onCancelReply?.()}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Drag Overlay */}
      {isDragging && canCompose && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.04)",
            border: "2px dashed #29a3ff",
            borderRadius: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: "#fff",
              border: "1px solid #e6e6e6",
              borderRadius: 12,
              padding: "10px 14px",
              display: "flex",
              gap: 8,
              boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
            }}
          >
            <Upload size={18} />
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              Drop file to attach
            </span>
          </div>
        </div>
      )}

      {/* Input Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: "#ffffff",
          border: "1px solid #e6e6e6",
          borderRadius: 22,
          padding: "8px 10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        {/* Preview */}
        {file &&
          (isImage(file) ? (
            <PreviewImage
              imagePreviewURL={imagePreviewURL}
              name={displayName || file.name}
              onRemove={() => {
                setFile(null);
                setDisplayName("");
                setImagePreviewURL(null);
                if (fileRef.current) fileRef.current.value = "";
              }}
            />
          ) : (
            <PreviewDoc
              name={displayName || file.name}
              onRemove={() => {
                setFile(null);
                setDisplayName("");
                if (fileRef.current) fileRef.current.value = "";
              }}
            />
          ))}

        {/* Emoji */}
        <button
          type="button"
          onClick={() => {
            if (!canCompose) return;
            setShowEmoji((v) => !v);
            setShowBar(false);
            textareaRef.current?.focus();
          }}
          style={{
            background: "transparent",
            border: "none",
            cursor: canCompose ? "pointer" : "not-allowed",
            padding: 6,
            opacity: canCompose ? 1 : 0.5,
          }}
        >
          <Smile size={22} />
        </button>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => {
            if (!canCompose) return;

            const value = e.target.value;
            setText(value);
            autoGrow(e.currentTarget);

            const cursor = e.target.selectionStart;
            const textUntilCursor = value.slice(0, cursor);

            const match = textUntilCursor.match(/@(\w*)$/);

            if (match) {
              const query = match[1].toLowerCase();
              setMentionQuery(query);
              setShowMention(true);

              const filtered = (categoryUsers || []).filter((u) =>
                u.name.toLowerCase().includes(query),
              );

              setMentionUsers(filtered);
            } else {
              setShowMention(false);
            }
          }}
          onKeyDown={handleKeyDown}
          onSelect={captureSelection}
          rows={1}
          placeholder={
            canCompose ? "Type a message" : "Select a category to start"
          }
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            fontSize: 15,
            padding: "6px 8px",
            background: "transparent",
            resize: "none",
            maxHeight: 160,
            color: "#1f2328",
          }}
        />

        {/* Hidden File Input */}
        <input
          type="file"
          ref={fileRef}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,image/png,image/jpeg,image/webp,image/gif"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) {
              onDropFiles([f]);
              handlePickFile(f);
            }
          }}
        />

        {/* Attach */}
        <button
          type="button"
          onClick={() => canCompose && fileRef.current?.click()}
          style={{
            background: "transparent",
            border: "none",
            cursor: canCompose ? "pointer" : "not-allowed",
            padding: 6,
            opacity: canCompose ? 1 : 0.5,
          }}
        >
          <Paperclip size={22} />
        </button>

        {/* Send */}
        <button
          type="submit"
          disabled={!canCompose || (!text && !file)}
          style={{
            background: "#20b857",
            color: "#fff",
            border: "none",
            borderRadius: 20,
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: !canCompose || (!text && !file) ? 0.6 : 1,
            cursor: !canCompose || (!text && !file) ? "not-allowed" : "pointer",
          }}
        >
          <Send size={18} />
        </button>

        {/* Floating Formatting Bar */}
        {showBar && canCompose && (
          <div
            ref={barRef}
            style={{
              position: "fixed",
              top: barPos.top,
              left: barPos.left,
              background: "#fff",
              border: "1px solid #e6e6e6",
              borderRadius: 14,
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
              padding: "6px 8px",
              display: "flex",
              gap: 8,
              zIndex: 9999,
            }}
          >
            <FmtBtn label="B" onClick={() => wrapSelection("*", "*")} />
            <FmtBtn label="I" onClick={() => wrapSelection("_", "_")} />
            <FmtBtn label="S" onClick={() => wrapSelection("~", "~")} />
            <FmtBtn label="M" onClick={monoWrap} />
            <Sep />
            <FmtBtn label="•" onClick={bulletList} />
            <FmtBtn label="1." onClick={numberList} />
          </div>
        )}

        {/* Emoji Picker */}
        {showEmoji && canCompose && (
          <EmojiPopover
            refEl={emojiRef}
            query={emojiQuery}
            setQuery={setEmojiQuery}
            recentEmojis={recentEmojis}
            setRecentEmojis={setRecentEmojis}
            insertTextAtCaret={insertTextAtCaret}
          />
        )}

        {/* Mention Dropdown */}
        {showMention && mentionUsers.length > 0 && (
          <div
            ref={mentionRef}
            style={{
              position: "absolute",
              bottom: "60px",
              left: "60px",
              width: 220,
              background: "#fff",
              border: "1px solid #e6e6e6",
              borderRadius: 10,
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              zIndex: 9999,
              maxHeight: 200,
              overflowY: "auto",
            }}
          >
            {mentionUsers.map((user) => (
              <div
                key={user._id}
                onClick={() => insertMention(user)}
                style={{
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontSize: 14,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f2f4f8")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                @{user.name}
              </div>
            ))}
          </div>
        )}
      </div>
    </form>
  );
}

/* ---------- Small UI Components ---------- */
function FmtBtn({ label, onClick }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      style={{
        border: "none",
        background: "transparent",
        fontWeight: 700,
        fontSize: 13,
        width: 28,
        height: 28,
        borderRadius: 8,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
function Sep() {
  return <div style={{ width: 1, background: "#ececec", margin: "0 4px" }} />;
}

function EmojiCell({ emoji, onClick }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onClick(emoji)}
      style={{
        width: 28,
        height: 28,
        borderRadius: 6,
        border: "1px solid #f0f0f0",
        background: "#fff",
        fontSize: 18,
        lineHeight: "26px",
        textAlign: "center",
        cursor: "pointer",
      }}
    >
      {emoji}
    </button>
  );
}

function PreviewImage({ imagePreviewURL, name, onRemove }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#f3faf5",
        border: "1px solid #e1f1e6",
        padding: "4px 6px",
        borderRadius: 12,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          overflow: "hidden",
          border: "1px solid #e5e5e5",
        }}
      >
        {imagePreviewURL && (
          <img
            src={imagePreviewURL}
            alt="preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
      </div>
      <span
        style={{
          fontSize: 12,
          maxWidth: 160,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
      <button
        type="button"
        onClick={onRemove}
        style={{ border: "none", background: "transparent" }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

function PreviewDoc({ name, onRemove }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: "#f4f7ff",
        border: "1px solid #e2e8ff",
        padding: "4px 6px",
        borderRadius: 12,
        maxWidth: "60%",
      }}
    >
      <FileText size={16} />
      <span
        style={{
          fontSize: 12,
          maxWidth: 160,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
      <button
        type="button"
        onClick={onRemove}
        style={{ border: "none", background: "transparent" }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

function EmojiPopover({
  refEl,
  query,
  setQuery,
  recentEmojis,
  setRecentEmojis,
  insertTextAtCaret,
}) {
  const filtered = EMOJI_BANK.filter((e) =>
    query.trim() ? e.toLowerCase().includes(query.trim().toLowerCase()) : true,
  );

  return (
    <div
      ref={refEl}
      style={{
        position: "absolute",
        bottom: "56px",
        left: "8px",
        width: "min(330px, 90vw)",
        maxHeight: 360,
        background: "#fff",
        border: "1px solid #e6e6e6",
        borderRadius: 14,
        boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
        padding: 10,
        zIndex: 9998,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <input
        placeholder="Search emoji"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{
          height: 34,
          border: "1px solid #e6e6e6",
          borderRadius: 8,
          padding: "0 10px",
          outline: "none",
          marginBottom: 8,
          fontSize: 14,
          width: "100%",
          boxSizing: "border-box",
        }}
      />

      {recentEmojis.length > 0 && !query && (
        <>
          <div style={{ fontSize: 12, color: "#818a98", margin: "4px 2px" }}>
            Recent
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(10, 1fr)",
              gap: 6,
              marginBottom: 8,
            }}
          >
            {recentEmojis.slice(0, 20).map((e) => (
              <EmojiCell
                key={`r-${e}`}
                emoji={e}
                onClick={(emo) => {
                  insertTextAtCaret(emo);
                  setRecentEmojis((prev) => {
                    const next = [emo, ...prev.filter((x) => x !== emo)];
                    saveRecents(next);
                    return next;
                  });
                }}
              />
            ))}
          </div>
        </>
      )}

      <div style={{ fontSize: 12, color: "#818a98", margin: "2px 2px 6px" }}>
        Smileys & People
      </div>

      <div style={{ overflow: "auto", paddingRight: 4 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(10, 1fr)",
            gap: 6,
            paddingBottom: 6,
          }}
        >
          {filtered.map((e) => (
            <EmojiCell
              key={e}
              emoji={e}
              onClick={(emo) => insertTextAtCaret(emo)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
